import { h } from 'preact';
import * as appStyles from '../../assets/global.scss';
import { Properties, WidgetTab } from '../../common';
import { actions, Actions, BookingState, connectWithStyles } from '../../store';
import { WithStyles } from '../../style';
import { Tab } from './Tab/Tab';
import * as styles from './Tabs.scss';
import Icon from '../../common/Icon/Icon';
import { useEffect, useState } from 'preact/hooks';
import { Localizer } from 'preact-i18n';
import { useDimension } from '../../common/hooks/useDimension';
import { FinText } from '../../i18n';
import { getTabToShow } from '../../utils/tabUtils';

export interface TabsProps extends Properties {
  enabledTabs: WidgetTab[];
}

interface StoreProps {
  currentTab: WidgetTab;
}

type EffectiveProps = TabsProps & StoreProps & Actions & WithStyles;

const mapStateToProps = ({ currentTab }: BookingState): StoreProps => ({
  currentTab,
});

export const Tabs = connectWithStyles<TabsProps>([appStyles, styles], { mapStateToProps, actions })((props) => {
  const { enabledTabs, currentTab, tx, setCurrentTab } = props as EffectiveProps;

  const tabToShow = getTabToShow(enabledTabs, currentTab);

  const [containerDimension, containerRef] = useDimension();
  const [contentDimension, contentRef] = useDimension();
  const [offset, setOffset] = useState(0);
  const [prevButtonVisible, setPrevButtonVisible] = useState(false);
  const [nextButtonVisible, setNextButtonVisible] = useState(false);

  const selectTab = (e: Event) => {
    const element = e.target as HTMLInputElement;
    const elementContainer = element.parentElement;
    const widgetTab = element.value as WidgetTab;
    setCurrentTab(widgetTab);
    elementContainer.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const scrollPrev = (): void => {
    containerRef.current.scrollLeft =
      containerRef.current.scrollLeft > containerDimension.width
        ? containerRef.current.scrollLeft - containerDimension.width
        : 0;
  };

  const scrollNext = (): void => {
    containerRef.current.scrollLeft =
      contentDimension.width > containerDimension.width + containerRef.current.scrollLeft
        ? containerRef.current.scrollLeft + containerDimension.width
        : contentDimension.width - containerDimension.width;
  };

  const updateOffset = (): void => {
    setOffset(containerRef.current.scrollLeft);
  };

  useEffect(() => {
    if (contentDimension.width > containerDimension.width) {
      if (containerDimension.width + offset >= Math.floor(contentDimension.width)) {
        setPrevButtonVisible(true);
        setNextButtonVisible(false);
      } else if (offset > 0) {
        setPrevButtonVisible(true);
        setNextButtonVisible(true);
      } else {
        setPrevButtonVisible(false);
        setNextButtonVisible(true);
      }
    } else {
      setPrevButtonVisible(false);
      setNextButtonVisible(false);
    }
  }, [containerDimension.width, contentDimension.width, offset]);
  return (
    <div class={tx(props.class)}>
      {enabledTabs.length > 1 && (
        <div
          data-test-tabs
          ref={containerRef}
          class={tx('tabs-container regular-type font-body-1 grey-600-border')}
          onScroll={updateOffset}
        >
          {prevButtonVisible && (
            <Localizer>
              <button
                class={tx('button-prev ps-xxsmall-x ps-xsmall-y ms-xxsmall-t white-bg rounded-right-medium shadow')}
                onClick={scrollPrev}
                title={(<FinText id="bookingWidgetTabs.previous" />) as any as string}
                aria-hidden
              >
                <Icon
                  iconName="chevron-left"
                  class={tx('is-xsmall icon ps-small-r border-right nordic-blue-900-text')}
                />
              </button>
            </Localizer>
          )}
          {nextButtonVisible && (
            <Localizer>
              <button
                className={tx('button-next ps-xxsmall-x ps-xsmall-y ms-xxsmall-t white-bg rounded-left-medium shadow')}
                onClick={scrollNext}
                title={(<FinText id="bookingWidgetTabs.next" />) as any as string}
                aria-hidden
              >
                <Icon
                  iconName="chevron-right"
                  class={tx('is-xsmall icon ps-small-l border-left nordic-blue-900-text')}
                />
              </button>
            </Localizer>
          )}
          <div ref={contentRef} class="flex--inline">
            {enabledTabs.map((tab, index) => (
              <Tab
                key={index}
                class={tx({ 'success-500-border': tab !== WidgetTab.AM, amBorder: tab === WidgetTab.AM })}
                value={tab}
                selected={tabToShow === tab}
                onSelect={selectTab}
                data-test-tab
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
